import { AudioScheduler } from './audioScheduler';
import { buildSchedule, getStateAtTime, getResultAtStopTime } from './resultCalculator';

// Owned by the test session, not by its screen. UI ticks never schedule audio.
export function createAudioTestRunner(protocol, {
  onState, onError, onFinish, onStatus, voiceEnabled = false, snapshot,
} = {}) {
  const schedule = buildSchedule(protocol);
  let timer = null, status = 'idle', interrupted = false, generation = 0, destroyed = false;
  const stopLoop = () => { if (timer) clearInterval(timer); timer = null; };
  const report = () => onState?.(getStateAtTime(schedule, Math.min(scheduler.getElapsed(), schedule.totalDuration)), status);
  const setStatus = next => { status = next; onStatus?.(next); };
  const scheduler = new AudioScheduler({ voiceEnabled, onError: e => {
    interrupted = true; stopLoop(); setStatus('error'); report(); onError?.(e);
  }});
  scheduler.setEvents(schedule.events);
  if (snapshot) {
    scheduler.restore(Math.min(Math.max(0, Number(snapshot.elapsed) || 0), schedule.totalDuration));
    status = ['running', 'starting', 'error', 'paused'].includes(snapshot.status) ? 'paused' : snapshot.status === 'finished' ? 'finished' : 'idle';
    interrupted = !!snapshot.interrupted || ['running', 'starting', 'error'].includes(snapshot.status);
  }
  function tick() {
    if (status !== 'running') return;
    report();
    if (scheduler.getElapsed() >= schedule.totalDuration + 2) {
      scheduler.finish(schedule.totalDuration); stopLoop(); setStatus('finished'); report(); onFinish?.();
    }
  }
  const beginLoop = () => { stopLoop(); timer = setInterval(tick, 100); tick(); };
  const result = () => ({ ...getResultAtStopTime(schedule, Math.min(scheduler.getElapsed(), schedule.totalDuration)),
    interrupted, protocolVersion: protocol.protocolVersion });
  return {
    schedule, getStatus: () => status, getElapsed: () => scheduler.getElapsed(),
    getSnapshot: () => ({ status, elapsed: Math.min(scheduler.getElapsed(), schedule.totalDuration), interrupted }),
    getState: () => getStateAtTime(schedule, Math.min(scheduler.getElapsed(), schedule.totalDuration)),
    getResultNow: result, setVoiceEnabled: v => scheduler.setVoiceEnabled(v),
    async start() {
      if (status !== 'idle' || destroyed) return false;
      const token = ++generation; setStatus('starting'); interrupted = false;
      const ok = await scheduler.unlock();
      if (token !== generation || destroyed) return false;
      if (!ok) { setStatus('error'); return false; }
      scheduler.setEvents(schedule.events); setStatus('running'); scheduler.start();
      if (status !== 'running') return false;
      beginLoop(); return true;
    },
    async seekTo(time) {
      const token = ++generation;
      if (!(await scheduler.unlock()) || token !== generation || destroyed) return false;
      interrupted = true; scheduler.setEvents(schedule.events); setStatus('running'); scheduler.seekTo(time);
      if (status !== 'running') return false;
      beginLoop(); return true;
    },
    async pause(markInterrupted = true) {
      if (destroyed || status === 'idle' || status === 'finished') return;
      generation++; if (markInterrupted) interrupted = true;
      stopLoop(); const pending = scheduler.pause(); setStatus('paused'); report(); await pending;
    },
    async resume() {
      if (!['paused', 'error'].includes(status) || destroyed) return false;
      const token = ++generation; interrupted = true;
      // Reserve the transition so repeated taps cannot start duplicate schedules.
      setStatus('starting');
      if (!(await scheduler.resume()) || token !== generation || destroyed) return false;
      if (status === 'error') return false;
      setStatus('running'); beginLoop(); return true;
    },
    reset() { generation++; scheduler.reset(); stopLoop(); interrupted = false; setStatus('idle'); report(); },
    destroy() { destroyed = true; generation++; stopLoop(); scheduler.destroy(); },
  };
}
export default createAudioTestRunner;
